/**
 * @quotech/ui — Toast (universal primitive)
 *
 * Lightweight global toast notifications for transient
 * success / error / warning / info feedback. Renders the toast
 * stack in a fixed bottom-right region with accessible
 * live-region semantics (role=region + aria-live=polite).
 *
 * Lifted from apps/steel + apps/sam in qf!2475 as the L2.3
 * takeoff item ("Toast primitive + bridge split — reduces drift
 * risk on a primitive every page depends on"). The pure
 * primitive lives here; tenants that need to bridge into a
 * notification-center / event bus do that via the `onToast` /
 * `listenForEvent` hooks below WITHOUT polluting the universal
 * API with their store-specific imports.
 *
 *   - apps/steel migrates straight to <ToastProvider> from this
 *     module — no extra layer needed.
 *   - apps/sam keeps its local apps/sam/src/shared/components/
 *     Toast.tsx as a thin BRIDGE wrapper that forwards every
 *     toast into pushNotification() (sam.notify center) and
 *     subscribes to the NOTIFY_EVENT window bus.
 *
 *   - apps/crm intentionally NOT migrated — it uses
 *     react-hot-toast under the hood with a different
 *     ({title, message, duration}) prop shape. Larger refactor
 *     deferred.
 *
 * USAGE (universal):
 *   import { ToastProvider, useToast } from '@quotech/ui'
 *
 *   function App() {
 *     return <ToastProvider><Routes /></ToastProvider>
 *   }
 *   function MyButton() {
 *     const { toast } = useToast()
 *     return <button onClick={() => toast('Saved.', 'success')}>…</button>
 *   }
 *
 * USAGE (with bridge):
 *   <ToastProvider
 *     onToast={({ message, type, options }) => {
 *       myStore.append({ title: options?.title ?? message, kind: type })
 *     }}
 *     listenForEvent="my-app.notify"
 *   >
 *     ...
 *   </ToastProvider>
 */

import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from 'react'
import { CheckCircle, XCircle, AlertTriangle, X, Info } from 'lucide-react'

import { cn } from '../lib/utils'
import { getToastVariantClasses, type ToastVariantKey } from '../lib/toastVariants'

export type ToastType = ToastVariantKey

/**
 * Open-shaped options bag. The universal primitive only reads
 * `title` (optional headline above the message) and `durationMs`
 * (auto-dismiss timeout, default 4000). Tenants that bridge into
 * their own notification stores can stash extra fields here
 * (e.g. `persist`, `source`) — they'll be forwarded verbatim
 * to the `onToast` callback.
 */
export interface ToastOptions {
  title?: string
  durationMs?: number
  [key: string]: unknown
}

interface ToastEntry {
  id: number
  message: string
  type: ToastType
  options?: ToastOptions
}

export interface ToastContextValue {
  toast: (message: string, type?: ToastType, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} })

export function useToast(): ToastContextValue {
  return useContext(ToastContext)
}

const ICON_MAP: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

// Classnames per variant come from the canonical shared palette in
// `../lib/toastVariants`. The palette is OPAQUE by construction —
// see BUG-2026-05-20-TOAST-CONTRAST for why the previous
// `bg-X-500/10 text-X-300` shape was unreadable on light-themed
// host pages (master SPA / qa / crm). Don't add a local override
// here — edit the shared module so every Toast.tsx in the repo
// (universal, tenant-shared, email-hub) stays in lockstep.

const DEFAULT_DURATION_MS = 4000

let nextId = 0

/**
 * Coerce ANY value to a render-safe string so React never receives
 * an object/array as a child. Without this, a single bad caller —
 * or a stale, JSON-deserialized payload — would crash the toast
 * surface with React error #31 (Objects are not valid as a React
 * child) and, because <ToastProvider> typically lives at the app
 * root, blank the entire app.
 *
 * Bug history: jake 2026-05-04 reported a blank screen after
 * trying to connect Outlook from a newly-invited team account.
 * Root cause was a stale notification body in localStorage holding
 * the raw Xero contact-reconcile dict {created, updated,
 * skipped_local_newer}; rendering it triggered #31 in the SAM
 * notification bell. Hardened the toast primitive at the same
 * time so a parallel issue can't take down the app via toasts.
 */
function toSafeText(value: unknown, maxLen = 200): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    const s = JSON.stringify(value)
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s
  } catch {
    return '[unrenderable]'
  }
}

export interface ToastProviderProps {
  children: ReactNode
  /**
   * Bridge hook — fires for every toast, BEFORE it auto-dismisses.
   * Use this to forward toasts into a tenant-local notification
   * center, analytics pipe, etc. Stays out of the universal API.
   */
  onToast?: (entry: { message: string; type: ToastType; options?: ToastOptions }) => void
  /**
   * Optional window-event name to subscribe to. Any
   * `window.dispatchEvent(new CustomEvent(name, { detail }))`
   * with `detail = { title, body?, kind? }` will surface as a
   * bottom-right toast. Lets non-React modules (zustand stores,
   * websocket handlers) trigger toasts without prop-drilling
   * the context.
   */
  listenForEvent?: string
}

export function ToastProvider({
  children,
  onToast,
  listenForEvent,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])

  const addToast = useCallback(
    (message: string, type: ToastType = 'success', options?: ToastOptions) => {
      const id = nextId++
      const duration = options?.durationMs ?? DEFAULT_DURATION_MS
      setToasts((prev) => [...prev, { id, message, type, options }])
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
      onToast?.({ message, type, options })
    },
    [onToast],
  )

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Bridge non-React notification dispatches via a custom window
  // event (opt-in via the listenForEvent prop). Any module can
  // window.dispatchEvent(new CustomEvent(name, { detail: {...} }))
  // and the toast surface picks it up.
  useEffect(() => {
    if (!listenForEvent) return
    if (typeof window === 'undefined') return
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { title: string; body?: string; kind?: ToastType }
        | undefined
      if (!detail || typeof detail.title !== 'string') return
      const id = nextId++
      const message = detail.body ? `${detail.title} — ${detail.body}` : detail.title
      const type = detail.kind ?? 'info'
      setToasts((prev) => [...prev, { id, message, type }])
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, DEFAULT_DURATION_MS)
    }
    window.addEventListener(listenForEvent, handler)
    return () => window.removeEventListener(listenForEvent, handler)
  }, [listenForEvent])

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      {/* COMPACT-TOAST (2026-05-22) — operator complaint: success
       *  toasts on small kanban gestures felt much too big. Matches
       *  packages/tenant-shared and packages/email-hub — kept in
       *  lockstep across all three Toast surfaces.
       *
       *  z-[120] + dynamic bottom offset — keep in lockstep with the
       *  email-hub and tenant-shared Toast twins (UX audit 2026-06,
       *  Layout #5/#6): toasts top the overlay scale (above context
       *  menus z-[110] and modal scrims z-50) and lift above the
       *  MobileDock / Email Hub bottom bar via the published height
       *  vars (both resolve to 0px on desktop). */}
      <div
        className="fixed right-4 z-[120] flex max-w-[280px] flex-col gap-1.5"
        style={{
          bottom:
            'calc(1rem + var(--mobile-dock-h, 0px) + var(--email-hub-bottom-bar-h, 0px))',
        }}
        role="region"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map((t) => {
          const Icon = ICON_MAP[t.type]
          return (
            <div
              key={t.id}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg border shadow-md',
                'animate-slide-up',
                getToastVariantClasses(t.type),
              )}
              role="status"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <p className="text-[12.5px] leading-snug flex-1">{toSafeText(t.message)}</p>
              <button
                type="button"
                onClick={() => removeToast(t.id)}
                className="shrink-0 opacity-50 hover:opacity-100 -mr-0.5"
                aria-label="Dismiss notification"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}
